/**
 * Email HTML helpers — sanitization en quoted-reply detectie.
 *
 * Geëxtraheerd uit InboxThreadPage.tsx in batch D sub-stap 9a om EmailBody +
 * CollapsibleQuote als losse component-files mogelijk te maken.
 */

import DOMPurify from 'dompurify';

const ALLOWED_TAGS = [
  'p', 'br', 'strong', 'b', 'em', 'i', 'u', 'a', 'ul', 'ol', 'li',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'pre', 'code',
  'table', 'thead', 'tbody', 'tr', 'th', 'td', 'div', 'span', 'img', 'hr',
];

const ALLOWED_ATTR = ['href', 'target', 'rel', 'src', 'alt', 'width', 'height', 'style', 'class'];

export function sanitizeHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
  });
}

/**
 * Split email HTML into body + quoted/signature parts.
 * Detecteert: Gmail "gmail_quote", Outlook "originalMessage", en common signature patterns
 * (Met vriendelijke groet, Groetjes, Kind regards) — voor "Toon geciteerd bericht"-toggle.
 */
const MOBIEL_STAART = /(<(?:div|p|br)[^>]*>(?:\s*<br[^>]*>\s*)*\s*(?:Verzonden (?:vanaf|vanuit|met)|Sent from|Verstuurd vanaf)\b[\s\S]*$)/i;

/**
 * "Verzonden vanuit Outlook voor iOS" staat vóór het citaat; de citaatsplitser
 * laat hem in de body staan. Tweede pas: de staart gaat mee naar het citaat.
 */
function zonderMobieleStaart(r: { body: string; quoted: string | null }): { body: string; quoted: string | null } {
  const m = r.body.match(MOBIEL_STAART);
  if (!m || m.index === undefined || m.index === 0) return r;
  return { body: r.body.slice(0, m.index), quoted: m[0] + (r.quoted ?? '') };
}

export function splitEmailContent(html: string): { body: string; quoted: string | null } {
  return zonderMobieleStaart(splitEmailContentRuw(html));
}

function splitEmailContentRuw(html: string): { body: string; quoted: string | null } {
  const div = document.createElement('div');
  div.innerHTML = html;

  // Gmail quoted reply
  const gmailQuote = div.querySelector('.gmail_quote');
  if (gmailQuote) {
    const quoted = gmailQuote.outerHTML;
    gmailQuote.remove();
    return { body: div.innerHTML, quoted };
  }

  // Apple Mail zonder kop: <blockquote type="cite"> en alles erna.
  const cite = div.querySelector('blockquote[type="cite"]');
  if (cite && cite.parentElement === div) {
    const nodes: Node[] = [];
    let node: Node | null = cite;
    while (node) { nodes.push(node); node = node.nextSibling; }
    const quoted = nodes.map(n => (n as Element).outerHTML || n.textContent || '').join('');
    nodes.forEach(n => n.parentNode?.removeChild(n));
    return { body: div.innerHTML, quoted };
  }

  // Outlook-style headers: "--- Oorspronkelijk bericht ---" of "Van: ... Verstuurd: ..."
  const allDivs = div.querySelectorAll('div, p, span');
  for (const el of allDivs) {
    const text = (el.textContent || '').trim();
    const isQuoteHeader =
      /^-{2,}\s*(Oorspronkelijk bericht|Original Message|Doorgestuurd bericht)/i.test(text) ||
      // Apple Mail / Kleda-reply: "Op 11 sep 2026 om 08:30 heeft Kleda <…> het
      // volgende geschreven:", "On 14 Sep 2026, at 14:18, Kleda wrote:"
      (text.length < 220 && /^(Op|On)\s.+\b(geschreven|schreef|wrote)\b\s*:?\s*$/is.test(text)) ||
      (/^(Van|From):\s*.+/i.test(text) && /Verzond|Verstuurd|Sent|Datum|Date/i.test(el.parentElement?.textContent || ''));
    if (isQuoteHeader) {
      const container = el.parentElement === div ? el : el.parentElement || el;
      const nodes: Node[] = [];
      let node: Node | null = container;
      while (node) {
        nodes.push(node);
        node = node.nextSibling;
      }
      const quoted = nodes.map(n => (n as Element).outerHTML || n.textContent || '').join('');
      nodes.forEach(n => n.parentNode?.removeChild(n));
      return { body: div.innerHTML, quoted };
    }
  }

  // Signature detection: "Met vriendelijke groet" / "Groetjes" / "Kind regards"
  const htmlStr = div.innerHTML;
  const sigPatterns = [
    /(<(?:div|p|br)[^>]*>(?:\s*<br[^>]*>\s*)*\s*(?:Met\s+)?[Vv]riendelijke\s+groet[\s\S]*$)/i,
    /(<(?:div|p|br)[^>]*>(?:\s*<br[^>]*>\s*)*\s*Groetjes[\s\S]*$)/i,
    /(<(?:div|p|br)[^>]*>(?:\s*<br[^>]*>\s*)*\s*Kind\s+regards[\s\S]*$)/i,
    // Mobiele mailclients: "Verzonden vanaf Outlook voor Android", "Sent from my iPhone"
    /(<(?:div|p|br)[^>]*>(?:\s*<br[^>]*>\s*)*\s*(?:Verzonden (?:vanaf|vanuit|met)|Sent from|Verstuurd vanaf)\b[\s\S]*$)/i,
  ];

  for (const pattern of sigPatterns) {
    const match = htmlStr.match(pattern);
    // Na "Verzonden vanaf Outlook" staat nooit eigen tekst meer; daar geldt de
    // 60%-grens niet (Celine 14-09: één regel eigen tekst, de rest client-staart).
    const mobiel = /Verzonden|Sent from|Verstuurd/i.test(match?.[0]?.slice(0, 120) ?? '');
    if (match && match.index !== undefined && (mobiel || match[0].length < htmlStr.length * 0.6)) {
      return {
        body: htmlStr.slice(0, match.index),
        quoted: match[0],
      };
    }
  }

  return { body: html, quoted: null };
}
